/**
 * Things the Android back button should close before it navigates anywhere.
 *
 * A sheet, a drawer or a dialog is the thing on top of the screen, so back
 * closes it first — the same as tapping outside it. Each one registers its
 * close function while it is open; the back handler takes the most recent.
 */

import { useEffect, useRef } from 'react';

const stack = [];

/**
 * Close the topmost open layer. Returns true when there was one.
 *
 * The entry is not popped here: it removes itself when its layer actually
 * closes. A layer that won't close (a dialog mid-payment) therefore keeps
 * absorbing back, rather than letting the screen under it navigate away.
 */
export function dismissTop() {
  const close = stack[stack.length - 1];
  if (!close) return false;
  close();
  return true;
}

/**
 * While `open`, the back button closes this layer by calling `onClose`.
 * The latest `onClose` is always the one used, so callers needn't memoise it.
 */
export function useBackDismiss(open, onClose) {
  const latest = useRef(onClose);
  latest.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const entry = () => latest.current?.();
    stack.push(entry);
    return () => {
      const i = stack.lastIndexOf(entry);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [open]);
}
